import React, { useCallback, useEffect, useRef } from 'react';
import { Button, Popup, Toast } from 'antd-mobile';
import { t } from 'i18n';

const MAX_CAPTURE_EDGE = 1920;
const JPEG_QUALITY = 0.88;

/** 部分 Android WebView 仅有 webkitGetUserMedia，无 navigator.mediaDevices */
function resolveGetUserMedia(): ((constraints: MediaStreamConstraints) => Promise<MediaStream>) | null {
  const md = typeof navigator !== 'undefined' ? navigator.mediaDevices : undefined;
  if (md?.getUserMedia) {
    return (constraints) => md.getUserMedia(constraints);
  }
  const nav = navigator as Navigator & {
    webkitGetUserMedia?: (
      c: MediaStreamConstraints,
      ok: (s: MediaStream) => void,
      err: (e: Error) => void
    ) => void;
    mozGetUserMedia?: (
      c: MediaStreamConstraints,
      ok: (s: MediaStream) => void,
      err: (e: Error) => void
    ) => void;
  };
  const legacy = nav.webkitGetUserMedia ?? nav.mozGetUserMedia;
  if (!legacy) {
    return null;
  }
  return (constraints) =>
    new Promise((resolve, reject) => {
      legacy.call(nav, constraints, resolve, reject);
    });
}

/** Popup 打开后 video 可能晚一帧挂载，避免误判为流失败 */
async function waitForVideoElement(
  videoRef: React.RefObject<HTMLVideoElement | null>,
  timeoutMs: number
): Promise<HTMLVideoElement | null> {
  const deadline = Date.now() + timeoutMs;
  for (;;) {
    const el = videoRef.current;
    if (el) {
      return el;
    }
    if (Date.now() >= deadline) {
      return videoRef.current;
    }
    await new Promise<void>((r) => {
      requestAnimationFrame(() => r());
    });
  }
}

function canvasToJpegFile(canvas: HTMLCanvasElement, quality: number): Promise<File> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('canvas toBlob failed'));
          return;
        }
        resolve(new File([blob], `h5-capture-${Date.now()}.jpg`, { type: 'image/jpeg' }));
      },
      'image/jpeg',
      quality
    );
  });
}

export interface H5PreviewShutterModalProps {
  visible: boolean;
  onUserCancel: () => void;
  onCaptured: (file: File) => void;
  onStreamError: () => void;
}

/**
 * H5 单次拍照：getUserMedia 预览 + canvas 抓拍（不走 native）。
 * 与扫码页同属「视频流开相机」，比 input[capture] 在 WebView 中更稳定。
 */
const H5PreviewShutterModal: React.FC<H5PreviewShutterModalProps> = ({
  visible,
  onUserCancel,
  onCaptured,
  onStreamError,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const onStreamErrorRef = useRef(onStreamError);
  const onUserCancelRef = useRef(onUserCancel);
  const onCapturedRef = useRef(onCaptured);

  onStreamErrorRef.current = onStreamError;
  onUserCancelRef.current = onUserCancel;
  onCapturedRef.current = onCaptured;

  useEffect(() => {
    if (!visible) {
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      const v = videoRef.current;
      if (v) {
        v.srcObject = null;
      }
      return;
    }

    let cancelled = false;

    const start = async () => {
      // 给 Popup 一帧时间挂上 <video>，减少「已拿到流但 ref 仍为 null」的竞态
      await new Promise<void>((r) => requestAnimationFrame(() => r()));
      if (cancelled) {
        return;
      }
      const getUserMedia = resolveGetUserMedia();
      if (!getUserMedia) {
        if (!cancelled) {
          onStreamErrorRef.current();
        }
        return;
      }
      try {
        let stream: MediaStream;
        try {
          stream = await getUserMedia({
            video: { facingMode: { ideal: 'environment' } },
            audio: false,
          });
        } catch {
          stream = await getUserMedia({
            video: true,
            audio: false,
          });
        }
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        const video = await waitForVideoElement(videoRef, 2500);
        if (!video) {
          stream.getTracks().forEach((track) => track.stop());
          if (!cancelled) {
            onStreamErrorRef.current();
          }
          return;
        }
        video.setAttribute('playsinline', 'true');
        video.playsInline = true;
        video.muted = true;
        video.srcObject = stream;
        await video.play();
      } catch {
        if (!cancelled) {
          onStreamErrorRef.current();
        }
      }
    };

    void start();

    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      const v = videoRef.current;
      if (v) {
        v.srcObject = null;
      }
    };
  }, [visible]);

  const notifyUserCancel = useCallback(() => {
    onUserCancelRef.current();
  }, []);

  const handleShutter = useCallback(async () => {
    const video = videoRef.current;
    if (!video || video.readyState < 2) {
      return;
    }
    let w = video.videoWidth;
    let h = video.videoHeight;
    if (w === 0 || h === 0) {
      return;
    }
    if (w > MAX_CAPTURE_EDGE || h > MAX_CAPTURE_EDGE) {
      const scale = MAX_CAPTURE_EDGE / Math.max(w, h);
      w = Math.round(w * scale);
      h = Math.round(h * scale);
    }
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      Toast.show({
        content: t('pages.delivery.detail.photo.cameraCaptureFailed', {
          errorMessage: 'canvas',
        }),
        position: 'bottom',
      });
      return;
    }
    ctx.drawImage(video, 0, 0, w, h);
    try {
      const file = await canvasToJpegFile(canvas, JPEG_QUALITY);
      onCapturedRef.current(file);
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : String(e);
      Toast.show({
        content: t('pages.delivery.detail.photo.cameraCaptureFailed', { errorMessage }),
        position: 'bottom',
      });
    }
  }, []);

  return (
    <Popup
      visible={visible}
      onClose={notifyUserCancel}
      closeOnMaskClick
      position="bottom"
      bodyStyle={{ height: '100dvh', padding: 0, display: 'flex', flexDirection: 'column', background: '#000' }}
    >
      <div className="flex flex-1 flex-col min-h-0 w-full">
        <video
          ref={videoRef}
          className="flex-1 w-full min-h-0 object-cover bg-black"
          playsInline
          muted
          autoPlay
        />
        <div className="flex shrink-0 gap-3 p-4 pb-6 bg-black">
          <Button block fill="outline" color="default" onClick={notifyUserCancel}>
            {t('pages.delivery.detail.cancel')}
          </Button>
          <Button block color="primary" onClick={() => void handleShutter()}>
            {t('pages.delivery.detail.completeTakePhoto')}
          </Button>
        </div>
      </div>
    </Popup>
  );
};

export default H5PreviewShutterModal;
